import { Workgroup } from "../../types/workgroup";
import { demoMembers, demoUserId } from "./members";

const now = Date.now();
const day = 86400000;

const makeWorkgroup = (
  id: string,
  name: string,
  desc: string,
  managerId: string,
  managerName: string,
  memberIds: string[],
  favorite: "Y" | "N" = "N",
  internalCount = 4,
  externalCount = 0,
  docCount = 10,
  parentWorkgroupId: string | null = null,
  rootWorkgroupId?: string,
  workgroupType: "GENERAL" | "HUB" | "PRIVATE" = "GENERAL"
): Workgroup => ({
  workgroupId: id,
  workgroupName: name,
  desc,
  managerId,
  managerName,
  favorite,
  members: demoMembers.filter((m) => memberIds.includes(m.userId)),
  withdrawMembers: null,
  invitePolicy: 0,
  externalUserCount: externalCount,
  internalUserCount: internalCount,
  status: 1,
  startDate: -1,
  endDate: -1,
  logDate: now - Math.floor(Math.random() * 30 * day),
  updateDate: now - Math.floor(Math.random() * 7 * day),
  isValidate: 1,
  docSize: docCount * 204800,
  docCount,
  readRequestCount: 0,
  webMeetingCount: 0,
  chatCount: 0,
  parentWorkgroupId,
  rootWorkgroupId: rootWorkgroupId ?? id,
  hasSubWorkgroup: false,
  workgroupType,
  workgroupJoined: true,
});

// ── Root workgroups ──────────────────────────────────────────────────────────

const wgProductDesign = makeWorkgroup(
  "wg-product-design",
  "Product Design",
  "UI/UX design assets and spec documents",
  demoUserId,
  "Demo User",
  [demoUserId, "alice.johnson", "emma.wilson", "kate.davis"],
  "Y",
  4,
  0,
  18
);

const wgEngineering = makeWorkgroup(
  "wg-engineering-core",
  "Engineering Core",
  "Backend and frontend architecture documents",
  "alice.johnson",
  "Alice Johnson",
  [demoUserId, "alice.johnson", "bob.chen", "james.brown", "david.kim"],
  "Y",
  5,
  0,
  25
);

const wgMarketing = makeWorkgroup(
  "wg-marketing-campaign",
  "Marketing Campaign",
  "Campaign materials and brand guidelines",
  "carol.smith",
  "Carol Smith",
  [demoUserId, "carol.smith", "kate.davis", "mia.rodriguez"],
  "N",
  4,
  0,
  12
);

const wgSecurity = makeWorkgroup(
  "wg-security-compliance",
  "Security & Compliance",
  "Security policies and audit reports",
  "david.kim",
  "David Kim",
  [demoUserId, "david.kim", "iris.taylor", "alice.johnson"],
  "N",
  3,
  1,
  9
);

const wgExecutive = makeWorkgroup(
  "wg-executive-projects",
  "Executive Projects",
  "Confidential executive-level strategic plans",
  demoUserId,
  "Demo User",
  [demoUserId, "alice.johnson", "grace.lee", "iris.taylor"],
  "Y",
  4,
  0,
  5
);

// ── Children of Product Design ───────────────────────────────────────────────

const wgDesignSystem = makeWorkgroup(
  "wg-design-system",
  "Design System",
  "Component library, tokens and guidelines",
  demoUserId,
  "Demo User",
  [demoUserId, "alice.johnson", "emma.wilson"],
  "N",
  3,
  0,
  8,
  wgProductDesign.workgroupId,
  wgProductDesign.workgroupId
);

const wgUxResearch = makeWorkgroup(
  "wg-ux-research",
  "UX Research",
  "User interviews, usability tests and insights",
  "emma.wilson",
  "Emma Wilson",
  [demoUserId, "emma.wilson", "kate.davis"],
  "N",
  3,
  0,
  6,
  wgProductDesign.workgroupId,
  wgProductDesign.workgroupId
);

// ── Children of Engineering Core ─────────────────────────────────────────────

const wgFrontend = makeWorkgroup(
  "wg-frontend-platform",
  "Frontend Platform",
  "React, TypeScript and build tooling",
  "alice.johnson",
  "Alice Johnson",
  [demoUserId, "alice.johnson", "bob.chen"],
  "N",
  3,
  0,
  15,
  wgEngineering.workgroupId,
  wgEngineering.workgroupId
);

const wgBackend = makeWorkgroup(
  "wg-backend-services",
  "Backend Services",
  "API design, microservices and infrastructure",
  "james.brown",
  "James Brown",
  [demoUserId, "james.brown", "david.kim", "bob.chen"],
  "N",
  4,
  0,
  20,
  wgEngineering.workgroupId,
  wgEngineering.workgroupId
);

// ── Children of Marketing Campaign ───────────────────────────────────────────

const wgBrand = makeWorkgroup(
  "wg-brand-identity",
  "Brand Identity",
  "Logos, color palette and brand voice",
  "carol.smith",
  "Carol Smith",
  [demoUserId, "carol.smith", "kate.davis"],
  "N",
  3,
  0,
  7,
  wgMarketing.workgroupId,
  wgMarketing.workgroupId
);

// ── Standalone root workgroups ────────────────────────────────────────────────

const wgCustomer = makeWorkgroup(
  "wg-customer-success",
  "Customer Success",
  "Customer onboarding and support documentation",
  "frank.patel",
  "Frank Patel",
  [demoUserId, "frank.patel", "mia.rodriguez"],
  "N",
  3,
  0,
  7
);

const wgFinance = makeWorkgroup(
  "wg-finance-ops",
  "Finance Operations",
  "Budget plans, forecasts and financial reports",
  "grace.lee",
  "Grace Lee",
  [demoUserId, "grace.lee", "noah.thompson"],
  "N",
  2,
  0,
  14
);

const wgHR = makeWorkgroup(
  "wg-hr-people",
  "HR & People",
  "Recruitment, onboarding and HR policies",
  "henry.martin",
  "Henry Martin",
  [demoUserId, "henry.martin", "kate.davis"],
  "N",
  2,
  0,
  6
);

const wgLegal = makeWorkgroup(
  "wg-legal-affairs",
  "Legal Affairs",
  "Contracts, NDAs and compliance materials",
  "iris.taylor",
  "Iris Taylor",
  [demoUserId, "iris.taylor"],
  "N",
  2,
  1,
  11
);

const wgDataAnalytics = makeWorkgroup(
  "wg-data-analytics",
  "Data Analytics",
  "Reports, dashboards and analytics notebooks",
  "liam.garcia",
  "Liam Garcia",
  [demoUserId, "liam.garcia", "noah.thompson", "grace.lee"],
  "N",
  4,
  0,
  20
);

// ── HUB + PRIVATE 워크그룹 ────────────────────────────────────────────────────

const wgSecretHub = makeWorkgroup(
  "wg-secret-hub",
  "Secret Projects",
  "비공개 허브 워크그룹",
  demoUserId,
  "Demo User",
  [demoUserId, "alice.johnson", "grace.lee"],
  "N",
  3,
  0,
  0,
  null,
  "wg-secret-hub",
  "HUB"
);

const wgPrivateAlice = makeWorkgroup(
  "wg-private-alice",
  `${wgSecretHub.workgroupName} _ Alice Johnson(alice.johnson)`,
  "",
  demoUserId,
  "Demo User",
  [demoUserId, "alice.johnson"],
  "N",
  2,
  0,
  4,
  wgSecretHub.workgroupId,
  wgSecretHub.workgroupId,
  "PRIVATE"
);

const wgPrivateGrace = makeWorkgroup(
  "wg-private-grace",
  `${wgSecretHub.workgroupName} _ Grace Lee(grace.lee)`,
  "",
  demoUserId,
  "Demo User",
  [demoUserId, "grace.lee"],
  "N",
  2,
  0,
  7,
  wgSecretHub.workgroupId,
  wgSecretHub.workgroupId,
  "PRIVATE"
);

wgSecretHub.hasSubWorkgroup = true;

// ── webMeetingCount 반영 (meetings.ts 와 동기화) ─────────────────────────────
wgProductDesign.webMeetingCount = 2;   // mtg-001, mtg-005
wgEngineering.webMeetingCount = 1;     // mtg-002
wgExecutive.webMeetingCount = 1;       // mtg-003
wgMarketing.webMeetingCount = 1;       // mtg-004

// ── hasSubWorkgroup 플래그를 루트 워크그룹에 설정
wgProductDesign.hasSubWorkgroup = true;
wgEngineering.hasSubWorkgroup = true;
wgMarketing.hasSubWorkgroup = true;

export const demoWorkgroups: Workgroup[] = [
  // roots (GENERAL)
  wgProductDesign,
  wgEngineering,
  wgMarketing,
  wgSecurity,
  wgExecutive,
  wgCustomer,
  wgFinance,
  wgHR,
  wgLegal,
  wgDataAnalytics,
  // children (GENERAL)
  wgDesignSystem,
  wgUxResearch,
  wgFrontend,
  wgBackend,
  wgBrand,
  // HUB + PRIVATE
  wgSecretHub,
  wgPrivateAlice,
  wgPrivateGrace,
];

/** 즐겨찾기된 워크그룹만 */
export const demoFavoriteWorkgroups = demoWorkgroups.filter(
  (wg) => wg.favorite === "Y"
);

/** 워크그룹 ID → 워크그룹 맵 */
export const demoWorkgroupMap = Object.fromEntries(
  demoWorkgroups.map((wg) => [wg.workgroupId, wg])
);
